import { useCallback, useState } from "react";

import type {
	BasicCustomMailItemSendOptionalInput,
	BasicCustomMailItemSendRequiredInput,
	LangCode,
} from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { SelectField } from "@/components/ui/form/fields/select-field";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";
import Send from "@/components/ui/icon/send";

import type { ComponentProps } from "react";

type Props = {
	/** プレビューボタン(「テスト送信」)を表示しないかどうか */
	hidePreviewButton?: boolean;

	label: string;
	dialogTitle: string;

	textValue: string | undefined;
	overrideValue: boolean | undefined;
	textErrorMessage: string | undefined;
	overrideErrorMessage: string | undefined;
	shouldSendEmail?: boolean | undefined;
	shouldSendEmailErrorMessage?: string | undefined;
	handleChangeText: (input: string) => void;
	handleChangeOverride: (input: boolean) => void;
	handleChangeShouldSendEmail?: (input: boolean) => void;
	sendTestEmail: ({ lang, email }: { lang: LangCode; email: string }) => Promise<void>;
	lang: LangCode;
	hotelEmailAddress: string;

	/** 設定モード: 共通設定に従うか、プラン独自設定か */
	overrideMode?: boolean;
	onOverrideModeChange?: (override: boolean) => void;

	/** 基本メール設定 */
	basicCustomMailSetting: BasicCustomMailItemSendRequiredInput | BasicCustomMailItemSendOptionalInput | undefined;
};

export const QuestionEmailTemplateItem = ({
	hidePreviewButton = false,
	label,
	textValue,
	overrideValue,
	textErrorMessage,
	overrideErrorMessage,
	shouldSendEmail,
	shouldSendEmailErrorMessage,
	handleChangeText,
	handleChangeOverride,
	handleChangeShouldSendEmail,
	sendTestEmail,
	dialogTitle,
	lang,
	hotelEmailAddress,
	overrideMode = overrideValue ?? false,
	onOverrideModeChange,
	basicCustomMailSetting,
}: Props) => {
	const [emailAddress, setEmailAddress] = useState<string>(hotelEmailAddress);
	const handleEmailAddressChange = useCallback((input: string) => {
		setEmailAddress(input);
	}, []);

	const [open, setOpen] = useState(false);

	const handleSendTestEmail = useCallback(async () => {
		await sendTestEmail({
			lang,
			email: emailAddress,
		});
	}, [emailAddress, lang, sendTestEmail]);

	const buttonVariant: ComponentProps<typeof Button>["variant"] = emailAddress ? "default" : "disabled";

	const handleClickPreviewButton = useCallback(() => {
		setOpen(true);
	}, []);

	const radioOptions = [
		{
			label: "送信する",
			value: true,
			isSelected: shouldSendEmail === true,
		},
		{
			label: "送信しない",
			value: false,
			isSelected: shouldSendEmail === false,
		},
	];
	const onRadioChange = useCallback(
		(value: boolean) => {
			handleChangeShouldSendEmail?.(value);
		},
		[handleChangeShouldSendEmail],
	);

	const handleOverrideModeChange = useCallback(
		(value: string) => {
			const isOverride = value === "override";
			onOverrideModeChange?.(isOverride);
			handleChangeOverride(isOverride);
		},
		[onOverrideModeChange, handleChangeOverride],
	);

	const overrideModeOptions: FormOptionType[] = [
		{
			id: "common",
			label: "基本メール設定に従う",
			value: "common",
		},
		{
			id: "override",
			label: "このプランの独自に設定",
			value: "override",
		},
	];

	const isBasicCustomMailItemSendOptionalInput = (
		basicCustomMailSetting: BasicCustomMailItemSendRequiredInput | BasicCustomMailItemSendOptionalInput | undefined,
	): basicCustomMailSetting is BasicCustomMailItemSendOptionalInput => {
		if (!basicCustomMailSetting) {
			return false;
		}

		if (!("shouldSendEmail" in basicCustomMailSetting)) {
			return false;
		}

		return true;
	};

	const isShowBasicMailText = isBasicCustomMailItemSendOptionalInput(basicCustomMailSetting)
		? basicCustomMailSetting.shouldSendEmail
		: true;

	return (
		<>
			{/* <div className="space-y-3"> */}
			<p className="text-sm font-semibold">{label}</p>
			<div className="flex items-center justify-between">
				<div className="border-gray-300 w-64 p-2 text-sm">
					<SelectField
						value={overrideMode ? "override" : "common"}
						options={overrideModeOptions}
						errorMessage={overrideErrorMessage}
						handleValueChange={handleOverrideModeChange}
					/>
				</div>
				{!hidePreviewButton && overrideMode && shouldSendEmail && !handleChangeShouldSendEmail && (
					<div>
						<Button
							variant={"outline"}
							type={"button"}
							onClick={handleClickPreviewButton}
							icon={<Send size="sm" />}
						>
							テスト送信
						</Button>
					</div>
				)}
				{overrideErrorMessage && <p className="text-red-500 text-xs">{overrideErrorMessage}</p>}
			</div>
			{/* 基本メール設定の文言表示 */}
			{!overrideMode && basicCustomMailSetting?.text && isShowBasicMailText && (
				<div className="space-y-3">
					<div className="bg-gray-50 rounded-md p-3">
						<div className="text-gray-800 whitespace-pre-wrap text-sm">{basicCustomMailSetting.text}</div>
					</div>
				</div>
			)}

			{overrideMode && (
				<div className="space-y-3 pl-2">
					{handleChangeShouldSendEmail ? (
						<div className="flex items-start justify-between">
							<div className="space-y-2">
								{radioOptions.map((option) => (
									<label
										key={`option-${label}-${option.label}`}
										className="flex cursor-pointer items-center space-x-2"
									>
										<input
											type="radio"
											name={`${label}-radio`}
											value={option.value.toString()}
											checked={option.isSelected}
											onChange={() => onRadioChange(option.value)}
											className="border-gray-300 h-4 w-4 text-black accent-black focus:ring-black"
										/>
										<span className="text-sm">{option.label}</span>
									</label>
								))}
								{shouldSendEmailErrorMessage && (
									<p className="text-red-500 text-xs">{shouldSendEmailErrorMessage}</p>
								)}
							</div>

							{/* ラジオボタンがある時は、ラジオボタンと横並びで表示 */}
							{!hidePreviewButton && shouldSendEmail && (
								<div>
									<Button
										variant={"outline"}
										type={"button"}
										onClick={handleClickPreviewButton}
										icon={<Send size="sm" />}
									>
										テスト送信
									</Button>
								</div>
							)}
						</div>
					) : null}

					{shouldSendEmail && (
						<TextAreaField
							value={textValue ?? undefined}
							handleChange={handleChangeText}
							errorMessage={textErrorMessage}
							placeholder="オリジナルの文面を追加したい場合はこちらに記入をしてください。送られる文面は右上の「テスト送信」から確認できます。"
						/>
					)}
				</div>
			)}

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="space-y-4">
					<DialogHeader>
						<DialogTitle>{dialogTitle}に送られるメールのテスト送信</DialogTitle>
						<DialogDescription className="text-xs">
							{/* テキストボックスに入力されているメールテンプレートの内容が上記のアドレス宛に送信されます。 */}
							{/* 確認後は必ず、右上の「保存」もしくは「更新」ボタンを押して、内容を確定させてください。*/}
							文言を追加していない場合も、デフォルトのメール内容を確認いただけます
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-8">
						<InputTextField
							label="送信先メールアドレス"
							value={emailAddress}
							handleChange={handleEmailAddressChange}
							errorMessage={undefined}
						/>
						<div className="flex justify-end">
							<Button type="button" size="lg" variant={buttonVariant} onClick={handleSendTestEmail}>
								送信
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

QuestionEmailTemplateItem.displayName = "QuestionEmailTemplateItem";
